<template>
    <targeting-router-link data-cy="invoice-link" :menu-items="menuItems" :to="routeTo">
        <slot/>
    </targeting-router-link>
</template>

<script setup lang="ts">
import { TargetingRouterLink } from '@tritt/multipanel'
import {computed, inject, Ref, ref} from 'vue'
import {INVOICE_VIEW} from '@/invoice'
import {CUSTOMER, SubAccount} from '@/subaccount/types/SubAccount'
import {getInvoice, getInvoiceBalance, useInvoiceService} from '@/invoice/invoice.service'
import {customers, findSubAccountByUuid, suppliers} from '@/subaccount/subAccount.service'
import {TransactionLine} from '@/types/transaction/TransactionLine'
import {CUSTOMER_ACCOUNT_NO, INCOME_ACCOUNT_NO} from '@/bookkeeping-account/accountTypes'
import {Invoice, OutgoingInvoice} from '@/invoice/types/Invoice'
import {Transaction} from '@/types/transaction/Transaction'
import {getTransactionsByInvoiceUuid} from '@/transaction/transaction.service'
import {PanelContentRequest} from '@tritt/multipanel'
import {IContextMenuItem} from '@tritt/multipanel'

const props = defineProps({
    invoiceUuid: {
        type: String,
        required: true
    },
    subAccountUuid: {
        type: String,
        required: true
    }
})

const routeTo = computed<PanelContentRequest>(() => ({
    componentName: INVOICE_VIEW.componentName,
    queryValues: {invoiceUuid: props.invoiceUuid},
}))

const invoiceBalance = ref()
const invoice = ref<Invoice | OutgoingInvoice>()

const subAccount = ref<SubAccount>()
const transactions = ref<Array<Transaction>>([])

const panelId: Ref<number> | undefined = inject('panelId', ref(1))
const {payInvoice, creditInvoice, creditInvoiceV2} = useInvoiceService()

const findTransactionLinesForInvoice = async () => {
    const searchParams = {
        query: null,
        subAccountUuid: subAccount?.value?.uuid,
        invoiceUuid: props.invoiceUuid,
        fromDate: '2000-01-01',
        toDate: '2099-12-31'
    }
    const result = await getTransactionsByInvoiceUuid(
        searchParams
    )
    transactions.value = result || []
}

const isOutgoingInvoice = computed(() => {
    if (!customers.value.length && !suppliers.value.length) {
        return false
    }
    const subAccount = findSubAccountByUuid(props.subAccountUuid)
    return subAccount ? subAccount.relationType === CUSTOMER : false
})

const isOutgoing = (invoice: Invoice): invoice is OutgoingInvoice => {
    return invoice.type === 'outgoing'
}

const findCustomerLineUuid = (lines: TransactionLine[]) => {
    return lines.find(l => l.accountNo === CUSTOMER_ACCOUNT_NO)?.uuid
}

const createCreditingInvoice = async () => {
    invoiceBalance.value = await getInvoiceBalance(props.invoiceUuid)
    invoice.value = await getInvoice(props.invoiceUuid)
    subAccount.value = findSubAccountByUuid(invoiceBalance?.value?.subAccountUuid)
    await findTransactionLinesForInvoice()
    if (invoice.value && isOutgoing(invoice.value) && invoice.value.invoiceLines.every(l => l.accountNo && l.productUuid)) {
        await creditInvoiceV2(invoiceBalance.value, invoice.value.invoiceLines, panelId.value + 1, findCustomerLineUuid(transactions.value[0].transactionLines))
    } else {
        const incomeTransactionLines: TransactionLine[] = []
        transactions.value.forEach(t => {
            incomeTransactionLines.push(...t.transactionLines.filter(l => l.accountNo === INCOME_ACCOUNT_NO))
        })
        await creditInvoice(invoiceBalance.value, incomeTransactionLines, panelId.value + 1, findCustomerLineUuid(transactions.value[0].transactionLines))
    }
}

const createPaymentTransaction = async () => {
    invoiceBalance.value = await getInvoiceBalance(props.invoiceUuid)
    if (invoiceBalance?.value?.balance !== 0) {
        await payInvoice(invoiceBalance.value, panelId.value + 1)
    }
}

const menuItems = computed(() => {
    const mItems: IContextMenuItem[] = []

    if (isOutgoingInvoice.value) {
        mItems.push({
            label: 'Lag kreditnota',
            ['data-cy']: 'cm-create-crediting-invoice',
            onSelect: createCreditingInvoice,
        })
    }

    mItems.push({
        label: 'Registrer betaling',
        ['data-cy']: 'cm-register-payment-invoice',
        onSelect: createPaymentTransaction,
    })

    return mItems
})

</script>

<style scoped>

</style>